import type { AnyAction } from 'redux';
import { eventChannel, type Channel, type Task } from 'redux-saga';
import { actionChannel, cancel, fork, select, take, takeEvery } from 'redux-saga/effects';

import config from '@/config/index';
import { api } from '@/modules/api/fetcher';
import { authSessionEnd, authSessionStart } from '@/modules/auth/services/actions';
import { visibilityEventListener } from '@/services/utilities/visibilityListener';

import { selectWakeUpFlag } from '../slice';

const MIN_TIME_DIFF = 10 * 60; // 10 min

const createVisibilityChannel = () =>
    eventChannel<boolean>(emit => {
        const listener = visibilityEventListener(visible => {
            emit(visible);
        });

        return listener.remove;
    });

export function* worker() {
    const channel = createVisibilityChannel();

    try {
        while (true) {
            const visible: boolean = yield take(channel);

            if (visible) {
                const { timestamp }: { timestamp: number } = yield select(selectWakeUpFlag);

                const now = new Date().getTime();

                const timeDiff = Math.max(0, Math.ceil((now - timestamp) / 1000));

                if (timeDiff < MIN_TIME_DIFF) {
                    continue;
                }

                yield* api().get(config.api.root);
            }
        }
    } finally {
        channel.close();
    }
}

export function* wakeup() {
    const channel: Channel<AnyAction> = yield actionChannel([authSessionStart, authSessionEnd]);

    try {
        let task: Task;

        yield takeEvery(channel, function* (action: AnyAction) {
            if (action.type === authSessionStart) {
                task = yield fork(worker);
            } else if (action.type === authSessionEnd) {
                yield cancel(task);
            }
        });
    } catch {
        channel.close();
    }
}
